<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-1">RAM snapshots</h1>
        <p class="text--secondary mb-4">
          Monitoring mémoire système. Snapshots écrits sur franchissement de palier
          RAM/swap (alertes Teams) ou périodiquement comme baseline (silencieux).
        </p>
      </v-col>
    </v-row>

    <!-- KPIs -->
    <v-row dense>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text--secondary caption">RAM used (dernier snapshot)</div>
            <div class="text-h5 mt-1">
              <span v-if="latest">
                {{ (latest.mem_used_mb / 1024).toFixed(2) }} Go
              </span>
              <span v-else class="text--secondary">—</span>
            </div>
            <div v-if="latest" class="caption text--secondary">
              sur {{ (latest.mem_total_mb / 1024).toFixed(2) }} Go
              · {{ formatDate(latest.created_at) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text--secondary caption">Dernier palier franchi</div>
            <div class="text-h5 mt-1">
              <span v-if="lastEvent">
                {{ lastEvent.threshold_gb }} Go
                <v-chip
                  x-small
                  :color="lastEvent.trigger_type === 'swap' ? 'purple' : 'orange'"
                  dark
                  class="ml-1"
                >
                  {{ lastEvent.trigger_type }}
                </v-chip>
              </span>
              <span v-else class="text--secondary">aucun</span>
            </div>
            <div v-if="lastEvent" class="caption text--secondary">
              {{ formatDate(lastEvent.created_at) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text--secondary caption">Snapshots 24h</div>
            <div class="text-h5 mt-1">{{ counts.last24h }}</div>
            <div class="caption text--secondary">
              dont {{ counts.events24h }} palier(s)
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text--secondary caption">Snapshots 7j</div>
            <div class="text-h5 mt-1">{{ counts.last7d }}</div>
            <div class="caption text--secondary">
              dont {{ counts.events7d }} palier(s)
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Timeline -->
    <v-row>
      <v-col cols="12">
        <MemUsageTimelineChart
          ref="timeline"
          @snapshot-click="openDetail"
        />
      </v-col>
    </v-row>

    <!-- Top growers -->
    <v-row>
      <v-col cols="12">
        <TopGrowersCard />
      </v-col>
    </v-row>

    <!-- Snapshots list -->
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            Liste des snapshots
            <v-spacer />
            <v-select
              v-model="filterTrigger"
              :items="triggerFilterItems"
              label="Trigger"
              dense
              outlined
              hide-details
              clearable
              style="max-width: 200px"
              class="mr-3"
            />
            <v-btn icon @click="reloadList" :loading="loadingList">
              <v-icon>{{ icons.mdiRefresh }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-data-table
            :items="list"
            :headers="listHeaders"
            :items-per-page="50"
            :loading="loadingList"
            dense
            @click:row="(row) => openDetail(row.id)"
            class="snapshots-list-table"
          >
            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
            <template #item.trigger_type="{ item }">
              <v-chip
                x-small
                :color="triggerColor(item.trigger_type)"
                dark
              >
                {{ item.trigger_type }}
              </v-chip>
            </template>
            <template #item.threshold_gb="{ item }">
              <span v-if="item.trigger_type === 'baseline'" class="text--secondary">—</span>
              <span v-else>{{ item.threshold_gb }} Go</span>
            </template>
            <template #item.mem_used_mb="{ item }">
              {{ (item.mem_used_mb / 1024).toFixed(2) }} Go
            </template>
            <template #item.swap_used_mb="{ item }">
              <span v-if="item.swap_total_mb > 0">
                {{ (item.swap_used_mb / 1024).toFixed(2) }} Go
              </span>
              <span v-else class="text--secondary">—</span>
            </template>
            <template #item.notified="{ item }">
              <v-icon v-if="item.notified" small color="success">
                {{ icons.mdiCheck }}
              </v-icon>
              <span v-else class="text--secondary">—</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <SnapshotDetailDialog
      v-model="detailOpen"
      :snapshot-id="detailId"
    />
  </v-container>
</template>

<script>
import axios from "@axios";
import { mdiRefresh, mdiCheck } from "@mdi/js";

import MemUsageTimelineChart from "@/components/admin/system/MemUsageTimelineChart.vue";
import TopGrowersCard from "@/components/admin/system/TopGrowersCard.vue";
import SnapshotDetailDialog from "@/components/admin/system/SnapshotDetailDialog.vue";

export default {
  name: "RamSnapshots",
  components: {
    MemUsageTimelineChart,
    TopGrowersCard,
    SnapshotDetailDialog,
  },
  data() {
    return {
      list: [],
      loadingList: false,
      filterTrigger: null,
      triggerFilterItems: [
        { text: "Tous", value: null },
        { text: "Threshold", value: "threshold" },
        { text: "Swap", value: "swap" },
        { text: "Baseline", value: "baseline" },
      ],
      detailOpen: false,
      detailId: null,
      icons: { mdiRefresh, mdiCheck },
      listHeaders: [
        { text: "Date", value: "created_at" },
        { text: "Trigger", value: "trigger_type" },
        { text: "Palier", value: "threshold_gb" },
        { text: "RAM used", value: "mem_used_mb", align: "end" },
        { text: "Swap used", value: "swap_used_mb", align: "end" },
        { text: "Processes", value: "process_count", align: "end" },
        { text: "Notif.", value: "notified", align: "center" },
      ],
      stats24h: { total: 0, events: 0 },
      stats7d: { total: 0, events: 0 },
    };
  },
  computed: {
    latest() {
      return this.list[0] || null;
    },
    lastEvent() {
      return (
        this.list.find((s) => s.trigger_type !== "baseline") || null
      );
    },
    counts() {
      return {
        last24h: this.stats24h.total,
        events24h: this.stats24h.events,
        last7d: this.stats7d.total,
        events7d: this.stats7d.events,
      };
    },
  },
  watch: {
    filterTrigger() {
      this.reloadList();
    },
  },
  async mounted() {
    await Promise.all([this.reloadList(), this.loadStats()]);
  },
  methods: {
    async reloadList() {
      this.loadingList = true;
      try {
        const params = { limit: 200 };
        if (this.filterTrigger) params.trigger_type = this.filterTrigger;
        const { data } = await axios.get("/system/ram-snapshots/", { params });
        this.list = data.items || [];
      } catch (e) {
        this.list = [];
      } finally {
        this.loadingList = false;
      }
    },
    async loadStats() {
      const now = new Date();
      const day = new Date(now.getTime() - 86400 * 1000);
      const week = new Date(now.getTime() - 7 * 86400 * 1000);

      try {
        const [d24, d7] = await Promise.all([
          axios.get("/system/ram-snapshots/", {
            params: { limit: 500, from_date: day.toISOString() },
          }),
          axios.get("/system/ram-snapshots/", {
            params: { limit: 500, from_date: week.toISOString() },
          }),
        ]);
        const items24 = d24.data.items || [];
        const items7 = d7.data.items || [];
        this.stats24h = {
          total: items24.length,
          events: items24.filter((s) => s.trigger_type !== "baseline").length,
        };
        this.stats7d = {
          total: items7.length,
          events: items7.filter((s) => s.trigger_type !== "baseline").length,
        };
      } catch (e) {
        // best effort
      }
    },
    openDetail(id) {
      this.detailId = id;
      this.detailOpen = true;
    },
    formatDate(d) {
      return new Date(d).toLocaleString("fr-FR");
    },
    triggerColor(t) {
      if (t === "threshold") return "orange";
      if (t === "swap") return "purple";
      return "grey";
    },
  },
};
</script>

<style scoped>
.snapshots-list-table >>> tbody tr {
  cursor: pointer;
}
</style>
